import { Address } from "viem"

import { TBTC_API_ENDPOINT } from "../constants"

/**
 * Represents the metadata for a reveal operation.
 */
type RevealMetadata = {
  recipient: string
}

/**
 * Represents the JSON structure of a Bitcoin deposit receipt.
 */
type BitcoinDepositReceiptJson = {
  depositor: string
  blindingFactor: string
  walletPublicKeyHash: string
  refundPublicKeyHash: string
  refundLocktime: string
  extraData: string
}

/**
 * Represents the request payload for saving a reveal.
 */
export type SaveRevealRequest = {
  address: string
  revealInfo: BitcoinDepositReceiptJson
  metadata: RevealMetadata
  application: string
  loggedInUser?: string
}

type RevealData = {
  address: string
  revealInfo: unknown
  metadata: unknown
  application: string
  loggedInUser: string | null
  insertedAt: number
}

/**
 * Register deposit data in the tBTC API.
 * @param revealData Deposit data.
 * @returns True if the reveal was successfully saved to the database, false
 * otherwise.
 */
async function saveReveal(revealData: SaveRevealRequest): Promise<boolean> {
  const response = await fetch(new URL("reveals", TBTC_API_ENDPOINT), {
    method: "POST",
    body: JSON.stringify(revealData),
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  })

  if (!response.ok) throw new Error("Failed to save reveal")

  const { success } = (await response.json()) as { success: boolean }

  if (!success) throw new Error("Failed to save reveal")

  const savedRevealResponse = await fetch(
    new URL(`accounts/${revealData.address}/reveals`, TBTC_API_ENDPOINT),
    {
      method: "GET",
      credentials: "include",
    },
  )

  if (!savedRevealResponse.ok)
    throw new Error("Could not confirm that reveal data was saved correctly")

  const reveals = (await savedRevealResponse.json()) as RevealData[]

  const savedReveal = reveals.find(
    (reveal) =>
      reveal.address === revealData.address &&
      reveal.application === revealData.application &&
      reveal.loggedInUser === revealData.loggedInUser &&
      JSON.stringify(reveal.metadata) === JSON.stringify(revealData.metadata) &&
      JSON.stringify(revealData.revealInfo) ===
        JSON.stringify(reveal.revealInfo),
  )

  if (!savedReveal)
    throw new Error("Could not confirm that reveal data was saved correctly")

  return success && !!savedReveal
}

/**
 * Represents the request payload for creating a deposit.
 */
type CreateDepositRequest = {
  depositReceipt: BitcoinDepositReceiptJson
  recipient: Address
}
/**
 * Represents the status of a deposit.
 */
export enum DepositStatus {
  Queued = "QUEUED",
  Initialized = "INITIALIZED",
  Finalized = "FINALIZED",
  Cancelled = "CANCELLED",
}

/**
 * Represents the response structure of a deposit creation request.
 */
type CreateDepositResponse = {
  depositId: string
  transactionHash: string
  outputIndex: number
}

async function getDepositById(
  depositId: string,
): Promise<CreateDepositResponse> {
  const response = await fetch(
    new URL(`deposits/${depositId}`, TBTC_API_ENDPOINT),
    {
      method: "GET",
      credentials: "include",
    },
  )

  if (!response.ok) throw new Error("Failed to find deposit by id")

  const responseData = (await response.json()) as CreateDepositResponse

  return responseData
}

/**
 * Initiate a bitcoin deposit in the tBTC API.
 * @param depositData Data of the deposit.
 * @returns Details of the initiated deposit.
 */
async function createDeposit(
  depositData: CreateDepositRequest,
): Promise<CreateDepositResponse> {
  const data = {
    ...depositData,
    depositOwner: depositData.recipient,
  }

  const response = await fetch(new URL("deposits", TBTC_API_ENDPOINT), {
    method: "POST",
    body: JSON.stringify(data),
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  })

  if (!response.ok) throw new Error("Failed to create deposit")

  const responseData = (await response.json()) as CreateDepositResponse

  return {
    depositId: responseData.depositId,
    transactionHash: responseData.transactionHash,
    outputIndex: responseData.outputIndex,
  }
}

export default {
  saveReveal,
  createDeposit,
  getDepositById,
}
